import Component from '@glimmer/component';
import { service } from '@ember/service';
import { gt, eq } from 'ember-truth-helpers';
import { hash } from '@ember/helper';
import { t } from 'ember-intl';
import { filterBy } from '@nullvoxpopuli/ember-composable-helpers';
import {
  HdsPageHeader,
  HdsButton,
  HdsAlert,
  HdsApplicationState,
  HdsTable,
  HdsLinkInline,
  HdsBadge,
} from '@hashicorp/design-system-components/components';
import { CoreLayoutMain, LimitBanner, Flex } from 'core/components';
import { boxMargin, boxPadding } from 'core/modifiers';
import { resourceTypeFor } from 'core/helpers';
import { DEFAULT_ACCOUNT_ID } from 'billing-common/constants/billing';
import Actions from 'hcp/components/page/organizations/actions';

/**
 *
 * `contractActivation` indicates whether or not we are
 * within the self serve contract activation workflow.
 * @argument contractActivation;
 * @type {Boolean}
 * @required
 *
 */
/**
 *
 * `model` is an object an `organizations` key.
 * @argument model;
 * @type {Object}
 * @required
 *
 */
/**
 *
 * `PageOrganizationsList` renders a table of organizations.
 *
 *
 * ```
 * <Page::Organizations::List
 *   @contractActivation={{this.contractActivation}}
 *   @model={{this.model}}
 * />
 * ```
 *
 * @class PageOrganizationsList
 *
 */
export default class PageOrganizationsList extends Component {
  @service abilities;
  @service currentUser;
  @service intl;

  DEFAULT_ACCOUNT_ID = DEFAULT_ACCOUNT_ID;

  get canCreateOrg() {
    let organizations = this.args?.model?.organizations;
    return this.abilities.can('create organizations', organizations, {
      userId: this.args?.model?.userId,
      isSsoUser: this.args?.model?.isSsoUser,
    });
  }

  // Returns both orgName column and actions column if outside of the
  // contract activation workflow. Returns only orgName if inside the
  // activation workflow.
  get columns() {
    const orgName = [
      {
        key: 'name',
        label: this.intl.t(
          'components.page.organizations.list.table.headers.name'
        ),
      },
    ];
    const actions = [
      {
        key: 'actions',
        label: this.intl.t(
          'components.page.organizations.list.table.headers.options'
        ),
        align: 'right',
      },
    ];

    return this.args?.contractActivation ? orgName : [...orgName, ...actions];
  }

  // returns either all orgs or a filtered list if within the
  // contract activation flow - orgs should NOT appear in the
  // contract activation flow if they are already on Flex,
  // have an upcoming Flex transition or are delinquent
  get organizations() {
    const orgs = this.args?.model?.organizations;

    const orgsAvailableForFlexActivation = orgs.filter((org) => {
      return org.canActivateFlex;
    });

    return this.args?.contractActivation
      ? orgsAvailableForFlexActivation
      : orgs;
  }

  get shouldShowOrgCreateButton() {
    return (
      this.canCreateOrg &&
      !this.args?.contractActivation &&
      this.organizations.length > 0
    );
  }
  <template>
    <CoreLayoutMain>
      <:header>
        <HdsPageHeader as |PH|>
          <PH.Title>
            {{t "components.page.organizations.list.title"}}
          </PH.Title>
          {{#if this.shouldShowOrgCreateButton}}
            <PH.Actions>
              <HdsButton
                @color="primary"
                @icon="plus"
                @iconPosition="leading"
                @route="cloud.orgs.create"
                @text={{t "components.page.organizations.create.action"}}
                data-test-organization-create
              />
            </PH.Actions>
          {{/if}}
        </HdsPageHeader>
      </:header>
      <:alert>
        {{#if (gt this.organizations.length 0)}}
          {{#if @contractActivation}}
            <HdsAlert @type="inline" @color="highlight" as |A|>
              <A.Title>
                {{t
                  "components.page.organizations.list.contract-activation.alert.title"
                }}
              </A.Title>
              <A.Description>
                {{t
                  "components.page.organizations.list.contract-activation.alert.description"
                }}
              </A.Description>
            </HdsAlert>
          {{else if @model.isSsoUser}}
            <HdsAlert @type="inline" @icon="info" as |A|>
              <A.Title data-test-sso-permissions-banner>
                {{t
                  "components.page.organizations.list.alert.sso-permissions-description"
                }}
              </A.Title>
              {{#let
                (filterBy "tfcSynced" true this.organizations)
                as |syncedOrganizations|
              }}
                {{#if syncedOrganizations.length}}
                  <A.Description data-test-sso-permissions-banner-tfc-synced>
                    {{t
                      "components.page.organizations.list.alert.sso-permissions-description-tfc-synced.steps"
                    }}
                    <ol class="inline">
                      <li>
                        {{t
                          "components.page.organizations.list.alert.sso-permissions-description-tfc-synced.1"
                          htmlSafe=true
                        }}
                      </li>
                      <li>
                        {{t
                          "components.page.organizations.list.alert.sso-permissions-description-tfc-synced.2"
                          htmlSafe=true
                        }}
                      </li>
                      <li>
                        {{t
                          "components.page.organizations.list.alert.sso-permissions-description-tfc-synced.3"
                          htmlSafe=true
                        }}
                      </li>
                    </ol>
                  </A.Description>
                {{/if}}
              {{/let}}
            </HdsAlert>
          {{else}}
            <LimitBanner
              @count={{this.currentUser.ownedOrganizations.length}}
              @padding="0"
              @variant={{resourceTypeFor "organization"}}
            />
          {{/if}}
        {{/if}}
      </:alert>
      <:body>
        {{#if (eq this.organizations.length 0)}}
          <Flex
            @alignItems="center"
            @direction="column"
            @grow={{1}}
            @justifyContent="center"
            {{boxMargin "2xl 0"}}
            {{boxPadding "2xl 0"}}
          >
            {{#if @contractActivation}}
              <HdsApplicationState
                data-test-contract-activation-empty-org-list
                as |A|
              >
                <A.Header
                  @icon="org"
                  @title={{t
                    "components.page.organizations.list.contract-activation.empty-state.header"
                  }}
                  data-test-contract-activation-empty-org-list-header
                />
                <A.Body
                  @text={{t
                    "components.page.organizations.list.contract-activation.empty-state.body"
                  }}
                  data-test-contract-activation-empty-org-list-body
                />
                <A.Footer as |F|>
                  <F.LinkStandalone
                    @icon="org"
                    @iconPosition="leading"
                    @query={{hash contract_activation=null}}
                    @route="cloud.orgs.list"
                    @text={{t
                      "components.page.organizations.list.contract-activation.empty-state.view-all-orgs"
                    }}
                    data-test-contract-activation-empty-org-list-view-all-orgs
                  />
                  <F.LinkStandalone
                    @icon="arrow-right"
                    @iconPosition="leading"
                    @href={{t
                      "components.page.organizations.common.contact-sales.link-href"
                    }}
                    @text={{t
                      "components.page.organizations.common.contact-sales.link-text"
                    }}
                    data-test-contract-activation-empty-org-list-contact-sales
                  />
                </A.Footer>
              </HdsApplicationState>
            {{else}}
              {{! if the user is not in the contract activation flow }}
              <HdsApplicationState
                data-test-organization-list-empty-state
                as |A|
              >
                <A.Header
                  @icon="org"
                  @title={{t
                    "components.page.organizations.list.empty-state.header"
                  }}
                />
                <A.Body
                  @text={{t
                    "components.page.organizations.list.empty-state.body"
                  }}
                />
                {{#if this.canCreateOrg}}
                  <A.Footer as |F|>
                    <F.LinkStandalone
                      @icon="plus"
                      @iconPosition="leading"
                      @route="cloud.orgs.create"
                      @text={{t
                        "components.page.organizations.list.empty-state.footer"
                      }}
                      data-test-organization-list-empty-create
                    />
                  </A.Footer>
                {{/if}}
              </HdsApplicationState>
            {{/if}}
          </Flex>
        {{else}}
          {{! if org length is greater than zero}}
          <section class="organizations">
            <HdsTable
              @caption={{t "components.page.organizations.list.title"}}
              @columns={{this.columns}}
              @model={{this.organizations}}
              @valign="middle"
            >
              <:body as |B|>
                <B.Tr data-test-organization-item data-table-row>
                  <B.Td>
                    <Flex @direction="row" @gap="xs">
                      {{! We can assume that the billing account ID is default-account
                      because we do not have multiple billing accounts yet, and there is
                      only one place where we create a billing account:
                      https://github.com/hashicorp/cloud-ui/blob/acfe6a0dbeb0bd3c647ee18987cc201ba56ab097/addons/core/package/src/services/billing.js#L62-L84
                    }}
                      <HdsLinkInline
                        class="hds-font-weight-medium"
                        @color="secondary"
                        @model={{if
                          @contractActivation
                          this.DEFAULT_ACCOUNT_ID
                          B.data.id
                        }}
                        @query={{if
                          @contractActivation
                          (hash org_id=B.data.id)
                          undefined
                        }}
                        @route={{if
                          @contractActivation
                          "billing.accounts.account.payments.contract-activation"
                          "cloud.orgs.detail.index"
                        }}
                        data-test-org-link
                        data-test-organization-item-name
                      >
                        {{B.data.name}}
                      </HdsLinkInline>
                      {{#if (eq B.data.owner.user @model.userId)}}
                        <HdsBadge
                          @text={{t "components.page.organizations.list.owner"}}
                        />
                      {{/if}}
                    </Flex>
                  </B.Td>
                  {{#unless @contractActivation}}
                    <B.Td>
                      <Flex @justifyContent="end">
                        <Actions
                          @canRename={{B.data.canRename}}
                          @isToggleIcon={{true}}
                          @organization={{B.data}}
                        />
                      </Flex>
                    </B.Td>
                  {{/unless}}
                </B.Tr>
              </:body>
            </HdsTable>
          </section>
        {{/if}}
      </:body>
    </CoreLayoutMain>
  </template>
}
